import { AUTH_SWR_KEYS } from "../Utilities/swrKeys";
import { useCachedSWR } from "../Utilities/useCachedSWR";
import { useLogging } from "../../Shared/hooks/useLogging/useLogging";
import { LogLevel } from "../../Shared/hooks/useLogging/Logging-types";
/**
 * Retrieves payment methods data from the cache using SWR; this does not trigger a network request.
 * Data is hydrated by AuthenticatedDataHydrator on app load.
 *
 * @returns {
 *   data: PaymentMethodsResponse | undefined - Payment methods, gift cards, and config
 *   fetchError: Error | undefined - Error if fetch failed
 *   isLoading: boolean - True during initial load (no data yet)
 * }
 *
 * @example
 * const { data, isLoading, fetchError } = usePaymentMethodsQuery();
 *
 * if (isLoading) return <Loading />;
 * if (fetchError) return <Error />;
 *
 * const cards = data?.paymentMethods || [];
 * const giftCards = data?.giftCards || [];
 */
export const usePaymentMethodsQuery = () => {
    const { datadogLog } = useLogging();
    const { data, error: fetchError, isLoading, mutate, } = useCachedSWR(AUTH_SWR_KEYS.PAYMENT_METHODS, {
        onSuccess: (data) => {
            if (!data.success) {
                datadogLog("Failed to fetch payment methods.", LogLevel.ERROR, undefined);
                throw new Error("Something unexpected happened while loading payment methods.");
            }
        },
        onError: (error) => {
            datadogLog("Failed to fetch payment methods.", LogLevel.ERROR, undefined, error);
        },
    });
    return { data, fetchError, isLoading, mutate };
};
